import { AgendaScenarioId } from '../constants';
import { assertNever } from 'owa-assert';
import { lazyShowFullComposeInEditMode } from 'owa-calendar-compose-form-store';
import { deleteCalendarEventWithConfirm } from 'owa-calendar-event-actions-with-confirm';
import { canDelete, shouldOpenInCompose } from 'owa-calendar-event-capabilities';
import { getCalendarEventWithId } from 'owa-calendar-events-store';
import { FULL_COMPOSE_VIEW_TYPE_MODAL } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import { openItemPeek } from 'owa-calendar-peek';
import { showReadingPaneWithPerf } from 'owa-calendar-reading-pane-actions';
import { updateSelectedItem } from 'owa-calendar-surface';
import { lockSelectedItem } from 'owa-calendar-surface-dategrid';
import { lazyShowItemContextMenu } from 'owa-calendar-surface-contextmenu-actions';
import { getCalendarSurfaceStore } from 'owa-calendar-surface-surface-store';
import { CalendarEvent } from 'owa-calendar-types';
import { getUserMailboxInfo } from 'owa-client-ids';
import EventScope from 'owa-service/lib/contract/EventScope';
import * as React from 'react';
import {
    CalendarEventInteractionCallback,
    isClickCalendarEventCallbackPayload,
    isDoubleClickCalendarEventCallbackPayload,
    isRightClickCalendarEventCallbackPayload,
    isDeleteKeyCalendarEventCallbackPayload,
    CalendarEventCallbackPayload,
} from 'owa-calendar-event-interaction-schema';

export function useCalendarEventInteractionCallbacks(): CalendarEventInteractionCallback {
    return React.useCallback((payload: CalendarEventCallbackPayload) => {
        const { id } = payload;
        if (isClickCalendarEventCallbackPayload(payload)) {
            const { target } = payload;
            const calendarEvent = getCalendarEventWithId(id.Id);
            updateSelectedItem(calendarEvent, target, getCalendarSurfaceStore(), target);
            lockSelectedItem(getCalendarSurfaceStore());
            if (calendarEvent) {
                if (shouldOpenInCompose(calendarEvent)) {
                    openMeetingDraft(calendarEvent);
                } else {
                    openItemPeek.importAndExecute(calendarEvent);
                }
            }
        } else if (isDoubleClickCalendarEventCallbackPayload(payload)) {
            const calendarEvent = getCalendarEventWithId(id.Id);
            if (calendarEvent) {
                if (shouldOpenInCompose(calendarEvent)) {
                    openMeetingDraft(calendarEvent);
                } else {
                    showReadingPaneWithPerf(
                        { Id: id.Id, mailboxInfo: getUserMailboxInfo() },
                        'AgendaItemClicked'
                    );
                }
            }
        } else if (isRightClickCalendarEventCallbackPayload(payload)) {
            const { id, parentFolderId, evt } = payload;
            const calendarEvent = getCalendarEventWithId(id.Id);
            updateSelectedItem(
                calendarEvent,
                evt.currentTarget,
                getCalendarSurfaceStore(),
                evt.currentTarget
            );
            if (calendarEvent) {
                lazyShowItemContextMenu.importAndExecute(id, parentFolderId, evt);
            }
        } else if (isDeleteKeyCalendarEventCallbackPayload(payload)) {
            const { id, target, onDelete } = payload;
            const calendarEvent = getCalendarEventWithId(id.Id);
            updateSelectedItem(calendarEvent, target, getCalendarSurfaceStore(), target);
            lockSelectedItem(getCalendarSurfaceStore());
            if (canDelete(calendarEvent)) {
                deleteCalendarEventWithConfirm(
                    calendarEvent,
                    EventScope.Default,
                    false /* isItemSeriesMaster */,
                    AgendaScenarioId,
                    false /*isInPopOutWindow*/,
                    undefined /* onCancel */,
                    onDelete /* onConfirm */
                );
            }
        } else {
            assertNever(payload);
        }
    }, []);
}

function openMeetingDraft(calendarEvent: CalendarEvent) {
    lazyShowFullComposeInEditMode.importAndExecute(
        calendarEvent,
        calendarEvent.IsRecurring ? EventScope.AllInstancesInSeries : EventScope.Default,
        false /* itemHasUpdates */,
        'DraftAgenda',
        FULL_COMPOSE_VIEW_TYPE_MODAL,
        null /* extractionSourceId */
    );
}
